import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { BOARD_DISPLAY_NAME } from "@/config/browserBoard";
import { normalizeSearchPostAttachments } from "@/entities/thread/normalizeAttachments";
import type { SearchResponse } from "@/entities/thread/types";
import { useCatalogStore } from "@/features/catalog/stores/catalogStore";
import type { DesktopHistorySidebarHandle } from "@/features/history/components/DesktopHistorySidebar";
import { getPublicSearchPath } from "@/features/search/publicSearch";
import {
  createSearchPageMediator,
  isSearchPageEvent,
  type SearchPageEffect,
  type SearchPageEvent,
  type SearchPageEventNotifier,
} from "@/features/search/searchPageMediator";
import {
  normalizeSearchScope,
  type SearchScope,
} from "@/features/search/searchScope";
import { apiGet } from "@/shared/api/client";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { useIsDesktop } from "@/shared/hooks/useMediaQuery";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";

export interface SearchQueryPresentation {
  readonly data: SearchResponse | undefined;
  readonly error: unknown;
  readonly hasData: boolean;
  readonly isLoading: boolean;
  readonly isRetrying: boolean;
}

export interface SearchPagePresentation {
  readonly node: ReturnType<typeof useComponentNode>;
  readonly desktopHistorySidebarRef: React.RefObject<DesktopHistorySidebarHandle | null>;
  readonly input: string;
  readonly query: string;
  readonly scope: SearchScope;
  readonly isDesktop: boolean;
  readonly isSettingsOpen: boolean;
  readonly searchQuery: SearchQueryPresentation;
  readonly onEvent: SearchPageEventNotifier;
}

export function useSearchPagePresenter(): SearchPagePresentation {
  const { q = "", scope: serializedScope } = useSearch({ from: "/search" });
  const navigate = useNavigate({ from: "/search" });
  const isDesktop = useIsDesktop();
  const scope = normalizeSearchScope(serializedScope);
  const normalizedQuery = q.trim();
  const { setSearchQuery, setSearchScope } = useCatalogStore();
  useDocumentTitle(`投稿検索 - ${BOARD_DISPLAY_NAME}`);

  const searchQuery = useQuery({
    queryKey: ["public-post-search", normalizedQuery, scope],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet<SearchResponse>(getPublicSearchPath(normalizedQuery, scope), {
        signal,
        cache: "no-store",
      }).then((response) => ({
        ...response,
        posts: normalizeSearchPostAttachments(response.posts),
      })),
    enabled: normalizedQuery.length > 0,
    staleTime: 0,
  });

  const desktopHistorySidebarRef = useRef<DesktopHistorySidebarHandle>(null);
  const navigateRef = useRef(navigate);
  const setSearchQueryRef = useRef(setSearchQuery);
  const setSearchScopeRef = useRef(setSearchScope);
  const refetchRef = useRef<() => void>(() => undefined);
  navigateRef.current = navigate;
  setSearchQueryRef.current = setSearchQuery;
  setSearchScopeRef.current = setSearchScope;

  const effectRunner = useRef<(effect: SearchPageEffect) => void>(
    (): void => undefined,
  );
  effectRunner.current = (effect: SearchPageEffect): void => {
    switch (effect.type) {
      case "sync-catalog-search":
        setSearchQueryRef.current(effect.query);
        setSearchScopeRef.current(effect.scope);
        return;
      case "navigate-search":
        void navigateRef.current({
          search: {
            q: effect.query,
            scope: effect.scope,
          },
        });
        return;
      case "retry":
        refetchRef.current();
        return;
      case "toggle-sidebar":
        desktopHistorySidebarRef.current?.toggle();
        return;
      case "scroll":
        window.scrollTo({
          top:
            effect.edge === "top" ? 0 : document.documentElement.scrollHeight,
        });
    }
  };

  const [mediator] = useState(() =>
    createSearchPageMediator({
      initialQuery: q,
      isDesktop,
      runEffect: (effect: SearchPageEffect): void => {
        effectRunner.current(effect);
      },
    }),
  );
  useHierarchyLifetime(mediator);

  const node = useComponentNode((event) => {
    if (!isSearchPageEvent(event)) return "unhandled";
    return mediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled";
  });
  const state = useMediatorSnapshot(mediator);
  const onEvent = useCallback<SearchPageEventNotifier>(
    (event: SearchPageEvent): ReturnType<SearchPageEventNotifier> =>
      node.dispatch(event).status,
    [node],
  );

  useEffect(() => {
    const event: SearchPageEvent = {
      type: "search/query-synchronized",
      query: q,
      scope,
    };
    node.dispatch(event);
  }, [node, q, scope]);

  useEffect(() => {
    const event: SearchPageEvent = {
      type: "search/viewport-synchronized",
      isDesktop,
    };
    node.dispatch(event);
  }, [node, isDesktop]);

  useLayoutEffect(() => {
    refetchRef.current = (): void => {
      void searchQuery.refetch();
    };
  }, [searchQuery.refetch]);

  return {
    node,
    desktopHistorySidebarRef,
    input: state.draft.sourceQuery === q ? state.draft.value : q,
    query: q,
    scope,
    isDesktop,
    isSettingsOpen: state.isSettingsOpen,
    searchQuery: {
      data: searchQuery.data,
      error: searchQuery.error,
      hasData: searchQuery.data !== undefined,
      isLoading: searchQuery.isLoading,
      isRetrying: searchQuery.isFetching,
    },
    onEvent,
  };
}
